import type { JobSeekerProfileFragment } from "./__generated__/jobSeekerProfile.gqlgenerated";
import type { ViewjobQuery } from "../__generated__/viewjob.gqlgenerated";

import EmployerInsightsModel = component.employerinsights.EmployerInsightsModel;

export interface EmployerInsightsProctorData {
    isJsProVjProInsightsTogActive: boolean;
    isJsProMobVjEmpInsightsTstActive: boolean;
}
export const buildEmployerInsightsModel = (
    jobSeekerProfile: JobSeekerProfileFragment | null | undefined,
    viewjob: NonNullable<ViewjobQuery["viewjob"]>,
    proctorData: EmployerInsightsProctorData| undefined,
    companySummary?: string | null): EmployerInsightsModel | null => {

    const applicantInsightsAccess = jobSeekerProfile?.profile?.jsProPremiumAccess?.applicantInsights?.access;
    const isJsProVjProInsightsTogActive = proctorData?.isJsProVjProInsightsTogActive ?? false;
    const isJsProMobVjEmpInsightsTstActive = proctorData?.isJsProMobVjEmpInsightsTstActive ?? false;

    const hasEmployerInsightsAccess = checkEmployerInsightsAccess(applicantInsightsAccess, isJsProMobVjEmpInsightsTstActive);
    const hasConsolidatedProInsightsAccess = checkConsolidatedProInsightsAccess(applicantInsightsAccess, isJsProVjProInsightsTogActive);
    const hasAccess = hasEmployerInsightsAccess || hasConsolidatedProInsightsAccess;

    if (!hasAccess && !companySummary) {
        return null;
    }
    const source = viewjob.job?.source;
    const responsiveness = source?.responsiveness;
    const isActivelyReviewApplications = responsiveness?.isActivelyReviewed;
    const responseRate = responsiveness?.responseRate;
    const avgResponseTimeDays = responsiveness?.averageResponseTimeDays;
    const avgResponseTimeInSeconds = computeAverageResponseTimeInSeconds(avgResponseTimeDays);

    const employerInsightsModel: EmployerInsightsModel = {
        hasAccess: hasAccess,
        responseTimeInHours: computeAverageResponseTimeInHours(avgResponseTimeInSeconds),
        responseTimeInDays: avgResponseTimeDays ?? -1,
        percentageResponsiveness: parseResponseRate(responseRate),
        isActivelyReviewApplications: isActivelyReviewApplications ?? false,
        lastActiveTimeInDays: responsiveness?.lastActivityTimeInDays ?? -1,
        companySummary: companySummary ?? undefined,
    }

    if (!isResponsive(employerInsightsModel) && !hasConsolidatedProInsightsAccess && !companySummary) {
        return null;
    }

    return employerInsightsModel;
}

const checkEmployerInsightsAccess = (access: "DISABLED" | "ENABLED" | null | undefined, isJsProMobVjEmpInsightsTstActive: boolean): boolean => {
    return isJsProMobVjEmpInsightsTstActive && access === "ENABLED";
}

const checkConsolidatedProInsightsAccess = (access: "DISABLED" | "ENABLED" | null | undefined, isJsProVjProInsightsTogActive: boolean):boolean => {
    return isJsProVjProInsightsTogActive && access === "ENABLED";
}

const computeAverageResponseTimeInSeconds = (averageResponseTimeDays: number | null | undefined): number => {
    return averageResponseTimeDays ? averageResponseTimeDays * 24 * 60 * 60 : 0;
}

const computeAverageResponseTimeInHours = (averageResponseTimeInSeconds: number): number => {
    return averageResponseTimeInSeconds / 3600;
}

const parseResponseRate = (responseRate: number | null | undefined): number => {
    if (responseRate == null || responseRate < 0) {
        return -1;
    }
    const percentage = Math.round(responseRate * 100);
    return Math.min(percentage, 100);
}

const isResponsive = (employerInsightsModel: EmployerInsightsModel): boolean => {
    return (
        employerInsightsModel.responseTimeInDays !== -1
        && employerInsightsModel.responseTimeInDays <= 14
        && employerInsightsModel.percentageResponsiveness >= 50
    );
}

